import isEmpty from 'lodash/isEmpty';
import { memo, useCallback } from 'react';
import { shallowEqual } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { root } from '~/preloaded';
import settings from '~/settings';
import { TYPE_GLOBAL_SEARCH } from '~/constants';
import { getLastSeason, isGlobalSearchEnabled } from '~/helpers/ladder';
import { getSeasonInfo } from '~/helpers/seasons';
import { useAppSelector, useAppDispatch, type RootState } from '~/store';
import { autocompleteActions } from '~/store/slices/autocompleteSlice';
import { fetchAutompleteResultThunk } from '~/store/thunks/autocompleteThunks';
import { fetchSearchResultThunk } from '~/store/thunks/searchThunks';
import { fetchClan } from '~/Actions/ActionClanProfile';
import {
  actionsClanRating,
  getLeagueDivisionRating,
  toggleCrossRealmLadder,
  toggleCurrentRealm,
  toggleCurrentSeason,
  toggleCurrentSeasonType,
  toggleDivision,
  toggleLeague,
} from '~/Actions/ActionClanRating';
import { actionsSearch } from '~/Actions/ActionSearch';

import ClansRating from '~/UIKit/ClansRating/ClansRating';

import type { LeagueDivisionRatingType } from '~/Actions/ActionClanRating';
import type { SEASON_TYPES } from '~/constants';
import type { IClanData } from '~/types/declaration';

const getRatingRealm = (id: number, currentSeason: number, state: RootState) => {
  if (!state.clans.wowsLadder[id]) {
    return null;
  }
  const ratingArray = state.clans.wowsLadder[id].ratings.filter((rating) => rating.season_number === currentSeason);
  return ratingArray.length > 0 ? ratingArray[0].realm : null;
};

const stateSelector = (state: RootState) => {
  const ladder = getSeasonInfo(state.clansRating.currentSeason);
  const hasError = isEmpty(ladder) || !ladder;
  const currentSeason = state.clansRating.currentSeason || 0;
  const lastSeason = getLastSeason();
  const clanHomeRealm = settings.realm.code;
  const currentRealm = state.clansRating.currentRealm || (isGlobalSearchEnabled ? TYPE_GLOBAL_SEARCH : clanHomeRealm);
  const clanId = state.currentAccount.clanId;

  return {
    hasError: hasError,
    autocomplete: state.autocomplete,
    currentAccount: state.currentAccount,
    clanId: clanId,
    currentClanId: state.clansRating.currentClanId,
    leagues: ladder.leagues,
    seasonType: ladder.kind,
    currentRealm,
    currentLeague: state.clansRating.currentLeague,
    currentDivision: state.clansRating.currentDivision,
    isCrossRealmLadder: state.clansRating.isCrossRealmLadder,
    currentSeasonType: state.clansRating.currentSeasonType || lastSeason?.kind,
    currentSeason: currentSeason,
    ratingLists: state.clansRating.ratingLists,
    isError: state.clansRating.isError,
    isFetching: state.clansRating.isFetching,
    errorMessage: state.clansRating.errorMessage,
    wowsLadder: clanId ? state.clans.wowsLadder[clanId] : null,
    searchResult: state.searchResult,
    // TODO: Define clanHomeRealm by Ladder's settings (after ladder fixes)
    // clanHomeRealm: state.currentAccount.clanId ? state.clans.wowsLadder[state.currentAccount.clanId].realm : null,
    clanHomeRealm,
    clanBattleRealm: clanId ? getRatingRealm(clanId, currentSeason, state) : undefined,
  };
};

const ContainerBaseClansRating = () => {
  const {
    hasError,
    autocomplete,
    currentAccount,
    clanId,
    currentClanId,
    leagues,
    seasonType,
    currentRealm,
    currentLeague,
    currentDivision,
    isCrossRealmLadder,
    currentSeasonType,
    currentSeason,
    ratingLists,
    isError,
    isFetching,
    errorMessage,
    wowsLadder,
    searchResult,
    clanHomeRealm,
    clanBattleRealm,
  } = useAppSelector(stateSelector, shallowEqual);
  const dispatch = useAppDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const onDivisionChange = useCallback(
    (division: number, ladderIsUpdate: boolean = false) => {
      dispatch(toggleDivision(division, ladderIsUpdate));
    },
    [dispatch],
  );

  const onLeagueChange = useCallback(
    (league: number, ladderIsUpdate: boolean = false) => {
      dispatch(toggleLeague(league, ladderIsUpdate));
    },
    [dispatch],
  );

  const onLeagueAndDivisionChange = useCallback(
    ({ league, division, clanId, withGlobalSpinner, ownClan }: LeagueDivisionRatingType) => {
      void dispatch(getLeagueDivisionRating({ league, division, clanId }, withGlobalSpinner, ownClan));
    },
    [dispatch],
  );

  const onCrossRealmChange = useCallback(
    (isCrossRealm: boolean) => {
      dispatch(toggleCrossRealmLadder(isCrossRealm));
    },
    [dispatch],
  );

  const onSeasonTypeChange = useCallback(
    (seasonType: SEASON_TYPES) => {
      dispatch(actionsSearch.changeTerm(''));
      dispatch(toggleCurrentSeasonType(seasonType));
      dispatch(autocompleteActions.clearAutocompleteState());
      dispatch(actionsSearch.clearSearchState());
    },
    [dispatch],
  );

  const onSeasonChange = useCallback(
    (season: number) => {
      dispatch(actionsSearch.changeTerm(''));
      dispatch(toggleCurrentSeason(season));
      dispatch(autocompleteActions.clearAutocompleteState());
      dispatch(actionsSearch.clearSearchState());
    },
    [dispatch],
  );

  const onRealmChange = useCallback(
    (realm: string) => {
      dispatch(actionsSearch.changeTerm(''));
      dispatch(toggleCurrentRealm(realm));
      dispatch(autocompleteActions.clearAutocompleteState());
      dispatch(actionsSearch.clearSearchState());
    },
    [dispatch],
  );

  const onRender = useCallback(
    (clanId: number) => {
      void dispatch(fetchClan(clanId));
    },
    [dispatch],
  );

  const setCurrentClanId = useCallback(
    (clanId: number) => {
      dispatch(actionsClanRating.setCurrentClanId(clanId));
    },
    [dispatch],
  );

  const clearState = useCallback(() => {
    dispatch(autocompleteActions.clearAutocompleteState());
    dispatch(actionsSearch.clearSearchState());
  }, [dispatch]);

  const onAutocompleteItemClick = useCallback(
    (item: IClanData, accountId: number) => {
      dispatch(actionsSearch.appendTermToHistory(item, accountId));
    },
    [dispatch],
  );

  const onSearchInputChange = useCallback(
    (term: string) => {
      return dispatch(fetchAutompleteResultThunk({ term, type: 'clansRating' }));
    },
    [dispatch],
  );

  const onPageChanged = useCallback(
    (page: number, abortSignal?: AbortSignal) => {
      dispatch(actionsSearch.changePage(page));
      return dispatch(fetchSearchResultThunk({ type: 'clansRating', abortSignal }));
    },
    [dispatch],
  );

  const onReloadClick = useCallback(
    (abortSignal?: AbortSignal) => {
      return dispatch(fetchSearchResultThunk({ type: 'clansRating', abortSignal }));
    },
    [dispatch],
  );

  return (
    <ClansRating
      urlParams={params}
      navigate={navigate}
      hasError={hasError}
      autocomplete={autocomplete}
      currentAccount={currentAccount}
      clanId={clanId}
      currentClanId={currentClanId}
      wowsLadder={wowsLadder}
      leagues={leagues}
      seasonType={seasonType}
      currentRealm={currentRealm}
      currentLeague={currentLeague}
      currentDivision={currentDivision}
      currentSeasonType={currentSeasonType}
      isCrossRealmLadder={isCrossRealmLadder}
      currentSeason={currentSeason}
      ratingLists={ratingLists}
      isError={isError}
      isFetching={isFetching}
      errorMessage={errorMessage}
      searchResult={searchResult}
      root={root}
      clanHomeRealm={clanHomeRealm}
      clanBattleRealm={clanBattleRealm}
      setCurrentClanId={setCurrentClanId}
      clearState={clearState}
      onAutocompleteItemClick={onAutocompleteItemClick}
      onCrossRealmChange={onCrossRealmChange}
      onDivisionChange={onDivisionChange}
      onLeagueAndDivisionChange={onLeagueAndDivisionChange}
      onLeagueChange={onLeagueChange}
      onPageChanged={onPageChanged}
      onRealmChange={onRealmChange}
      onReloadClick={onReloadClick}
      onRender={onRender}
      onSearchInputChange={onSearchInputChange}
      onSeasonChange={onSeasonChange}
      onSeasonTypeChange={onSeasonTypeChange}
    />
  );
};

export default memo(ContainerBaseClansRating);
